import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, EMPTY, finalize, tap } from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { SalesOrder } from 'src/app/core/models/sales/sales-order.interface';
import { OrganizationService } from 'src/app/core/services/organization.service';

@Component({
  selector: 'app-organization-sales-orders-tab',
  templateUrl: './organization-sales-orders-tab.component.html',
  styleUrl: './organization-sales-orders-tab.component.scss',
  standalone: false,
})
export class OrganizationSalesOrdersTabComponent implements OnChanges {
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public organizationId = '';

  public readonly notFound = Constants.NotFound;
  public searchTerm = '';
  public isLoading = false;
  public pageSize = 10;
  public totalItems = 0;
  public readonly salesOrders$ = new BehaviorSubject<SalesOrder[]>([]);
  public readonly pagedSalesOrders$ = new BehaviorSubject<SalesOrder[]>([]);

  private page = 1;
  private filteredSalesOrders: SalesOrder[] = [];

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['companyId'] || changes['organizationId']) &&
      this.companyId &&
      this.organizationId
    ) {
      this.loadTabData();
    }
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.page = 1;
    this.applySearchAndPaging();
  }

  public onPageChange(page: number): void {
    this.page = page;
    this.applySearchAndPaging();
  }

  public onChangePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.applySearchAndPaging();
  }

  public openSalesOrder(order: SalesOrder): void {
    const salesOrderId = this.getOrderId(order);
    if (!salesOrderId) {
      return;
    }

    this.router.navigate([
      '/companies',
      this.companyId,
      'crm',
      'organizations',
      this.organizationId,
      'sales-orders',
      salesOrderId,
    ]);
  }

  private loadTabData(): void {
    this.isLoading = true;
    this.organizationService.setOrganizationsUrl(this.companyId);
    this.organizationService
      .getOrganization(this.organizationId)
      .pipe(
        tap((organization) => {
          this.salesOrders$.next((organization.salesOrders ?? []).map((order) => this.normalizeSalesOrder(order)));
          this.page = 1;
          this.applySearchAndPaging();
        }),
        catchError(() => {
          this.salesOrders$.next([]);
          this.pagedSalesOrders$.next([]);
          this.totalItems = 0;
          return EMPTY;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  private applySearchAndPaging(): void {
    const searchTerm = this.searchTerm.trim().toLowerCase();
    const allSalesOrders = this.salesOrders$.getValue();

    this.filteredSalesOrders = searchTerm.length
      ? allSalesOrders.filter((order) =>
          this.toSearchValue(order).includes(searchTerm),
        )
      : allSalesOrders;

    this.totalItems = this.filteredSalesOrders.length;

    if (this.totalItems === 0) {
      this.pagedSalesOrders$.next([]);
      return;
    }

    const totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    if (this.page > totalPages) {
      this.page = totalPages;
    }

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.pagedSalesOrders$.next(this.filteredSalesOrders.slice(start, end));
  }

  private toSearchValue(order: SalesOrder): string {
    return [
      order.number ?? '',
      order.customerNumber ?? '',
      order.orderDate ?? '',
      order.postingDate ?? '',
      order.requestedDeliveryDate ?? '',
    ]
      .join(' ')
      .toLowerCase();
  }

  private normalizeSalesOrder(order: SalesOrder): SalesOrder {
    const source = order as SalesOrder & { Id?: string };
    return {
      ...order,
      id: source.id || source.Id || '',
    };
  }

  private getOrderId(order: SalesOrder): string {
    const source = order as SalesOrder & { Id?: string };
    return source.id || source.Id || '';
  }
}
